import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Link,
  Backdrop,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Grid,
  useTheme,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import uploadFileToCloud from '../utils/upload-file';
import axios from 'axios';
import { ArrowBack, ArrowForward, CheckCircleOutline, DeleteOutlined, InfoOutlined } from '@mui/icons-material';
import ToastMessage from '../components/toast-message';
import HomeNavbar from '../components/home-nav-bar';
import SuccessDialog from '../components/success-dialog';
import { useNavigate } from 'react-router-dom';

function SubmitManuscript() {
  const theme = useTheme();
  const user = JSON.parse(localStorage.getItem('user'));
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    authors: [],
    file: null,
  });

  const steps = [
    'Contact Information',
    'Authors',
    'Upload Manuscript'
  ];

  const [activeStep, setActiveStep] = useState(0);

  const [acceptedTerms, setAcceptedTerms] = useState(false);


  const [loading, setLoading] = useState(false);
  
  const [status, setStatus] = useState({
    type: 0,
    message: ''
  });

  const clearToastMessge = () => {
    setStatus({
        type: 0,
        message: ''
    });
  };

  const [actionSuccessful, setActionSuccessful] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {

    if (!formData.title) {
      setStatus({
        type: 0,
        message: 'please fill in all fields'
      });
      return;
    }

    if (formData.authors.length < 1) {
      setStatus({
        type: 0,
        message: 'please add at least one author'
      });
      return;
    }

    for (const author of formData.authors) {
      if (!author.name.trim() || !author.email.trim()) {
         setStatus({
          type: 0,
          message: 'names and emails of all authors are required'
        });
      return;
      }
    }
  
    if (!formData.file) {
      setStatus({
        type: 0,
        message: 'please upload the manuscript file'
      });
      return;
    }

    if (!acceptedTerms) {
      setStatus({
        type: 0,
        message: 'You must accept the terms and conditions before submitting.'
      });
      return;
    }
    

    setLoading(true);
  
    try {
      // Generate unique file ID using current datetime
      const fileId = Date.now();
      const path = `wajls/pending_publications/${fileId}/${formData.file.name}`;
  
      // Upload file to Firebase Cloud Storage
      const fileUrl = await uploadFileToCloud(formData.file, path, (progress) => {
        // console.log(`Upload progress: ${progress.toFixed(2)}%`);
      });
  
      // Prepare the data to be sent to backend
      const submission = {
        publisher_id: user.id,
        name: formData.title,
        authors: formData.authors,
        file_url: fileUrl,
        file_path: path,
      };
  
      const response = await axios.post('https://api.wajls.com/client/v1/submit_manuscript.php', submission);

      
      if (response.data.success) {
        setActionSuccessful(true);
      }
      else {
        setStatus({
          type: 0,
          message: response.data.message
        });
      }

    } catch (error) {
      console.error();
    } finally {
      setLoading(false);
    }
  };

   const renderContactInfo = () => {
      return (
        <Box sx={{ mt: 4, width: '100%' }}>

          <Box
            sx={{
              backgroundColor: '#f5e58c',
              padding: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 3
            }}
          >
            <InfoOutlined fontSize="small" color="primary" />
              <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>
                After your article is approved, you'll be required to make a payment of ₦40,000 for it to finally go live on the website.
              </Typography>
            </Box>
  
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500, 
              mb: 3, 
              color: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              paddingBottom: 1,
              display: 'inline-block',
            }}
            
          >
            Basic Info
          </Typography>

        
          <TextField
            label="Article Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            fullWidth
            required
            sx={{ backgroundColor: 'white', mb: 3 }}
          />
    
        </Box>
      );
    };

  const renderAuthorsInfo = () => {
    const handleAddAuthor = () => {
      setFormData(prev => ({
        ...prev,
        authors: [...(prev.authors || []), { name: '', email: '' }]
      }));
    };

    const handleAuthorChange = (index, field, value) => {
      setFormData(prev => {
        const updated = [...prev.authors];
        updated[index][field] = value;
        return { ...prev, authors: updated };
      });
    };

    const handleRemoveAuthor = (index) => {
      setFormData(prev => ({
        ...prev,
        authors: prev.authors.filter((_, i) => i !== index)
      }));
    };

    return (
      <Box sx={{ mt: 4, width: '100%' }}>
      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 500, 
          mb: 3, 
          color: theme.palette.primary.main,
          borderBottom: `2px solid ${theme.palette.primary.main}`,
          paddingBottom: 1,
          display: 'inline-block'
        }}
      >
        Authors
      </Typography>
        
      <Box
        sx={{
          backgroundColor: '#c5fad8',
          padding: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          marginBottom: 3,
          borderRadius: 1,
        }}
      >
        <InfoOutlined fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>
          Please add all authors associated with this manuscript, including yourself if applicable.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {formData.authors.map((author, index) => (
          <Box
            key={index}
            sx={{
              position: 'relative',
              backgroundColor: '#fff',
              padding: 3,
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2 
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Author {index + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleRemoveAuthor(index)}
                sx={{
                  color: 'error.main',
                }}
              >
                <DeleteOutlined />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Full Name"
                variant="outlined"
                value={author.name}
                onChange={(e) => handleAuthorChange(index, 'name', e.target.value)}
                fullWidth
              />
              <TextField
                label="Email Address"
                variant="outlined"
                value={author.email}
                onChange={(e) => handleAuthorChange(index, 'email', e.target.value)}
                fullWidth
              />
            </Box>
          </Box>
        ))}

        <Button
          variant="contained"
          color="primary"
          onClick={handleAddAuthor}
          sx={{
            alignSelf: 'flex-start',
            mt: 1,
            borderRadius: 2,
            textTransform: 'none',
          }}
        >
          + Add Author
        </Button>
      </Box>
    </Box>
    );
  };


    const renderUpload = () => {
      return (
        <Box sx={{ mt: 4, width: '100%' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500, 
              mb: 3, 
              color: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              paddingBottom: 1,
              display: 'inline-block'
            }}
          >
            File Upload
          </Typography>
    
          <Stack spacing={2}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              sx={{ 
                textTransform: 'none', 
                justifyContent: 'flex-start', 
                backgroundColor: 'white',
                alignSelf: 'flex-start' 
              }}
            >
              {formData.file ? formData.file.name : 'Upload Manuscript (PDF or DOCX)'}
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                hidden
                onChange={handleFileChange}
              />
            </Button>
    
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I accept the{' '}
                  <Link href="/terms-and-conditions" target="_blank" underline="hover">
                    terms and conditions
                  </Link>
                </Typography>
              }
            />

          </Stack>
        </Box>
      );
    };
    
  
  

  return (
    <Box>
      <HomeNavbar/>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: '100vh',
        }}
      >

        {/* Left Section */}
        <Box
          sx={{
            flex: 1,
            backgroundImage: `url('https://images.unsplash.com/photo-1519337265831-281ec6cc8514')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: '#fff',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: 'center',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight={700} mb={2}>
              Make your research count
            </Typography>
            <Typography variant="body1" fontSize="1.1rem">
              Submit your manuscript for global impact. Join our community of trusted scholars.
            </Typography>
          </Box>
        </Box>

        {/* Right Section */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: 'grey.100',
          }}
        >
          {/* Horizontal stepper for both mobile and desktop */}
          <Box sx={{ width: '100%', mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel sx={{ fontWeight: 'bold' }}></StepLabel>
                </Step>
              ))}
            </Stepper>
          
          </Box>

          {activeStep === 0 && renderContactInfo()}
          {activeStep === 1 && renderAuthorsInfo()}
          {activeStep === 2 && renderUpload()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              startIcon={<ArrowBack />}
              sx={{ 
                px: 3,
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              color="primary"
              endIcon={activeStep === steps.length - 1 ? <CheckCircleOutline /> : <ArrowForward />}
              sx={{ 
                px: 3,
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {activeStep === steps.length - 1 ? 'Submit Application' : 'Continue'}
            </Button>
          </Box>

          <Link href="/publishing-guidelines" sx={{ color: '#1976d2', fontWeight: 500, textAlign: 'center', textDecoration: 'underline', marginTop: 2 }}>
            View our publishing guidelines
          </Link>.
        </Box>
      

      </Box>
    
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {status.message && <ToastMessage type={status.type} message={status.message} onClose={clearToastMessge}/>}

      <SuccessDialog open={actionSuccessful} article={true} email={formData.email} onClose={() => {
        setActionSuccessful(false);
        window.location.href = "/author-dashboard";
      }}/>

    </Box>
  );
}

export default SubmitManuscript;
